import React, { FC, useEffect, useRef } from 'react'
import { CardErrorReason } from '../../model'
import { Button, ColorTypes, Icon, Span, Text, useTooltip } from '@afterpaytouch/core'
import { useCheckoutColor } from '../../utils/color'
import { isNullOrUndefined, isUndefinedOrEmpty } from '@afterpay/utils'
import styles from './style.module.css'
import { CardAsterisk } from './CardAsterisk'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation } from '../../utils/convergence'
import { useCohort, useApplePayPayment } from '../../state'
import { Cohort } from '@afterpaytouch/portal-api'
import { useFlag } from '@afterpaytouch/feature'
import { Features } from '../../model/features'
import { MaskedElement } from '@afterpaytouch/integrations'

interface PaymentMethodInfoProps {
  paymentMethodMasks?: string | null
  expiryDate?: string
  hasSeparator?: boolean
  errorKey?: CardErrorReason | null
  disabled?: boolean
  cashTag?: string
}

interface PaymentMethodMaskedTextProps {
  text?: string
  subtextColor: ColorTypes
  testNameSpace: string
}

const SEPERATOR = '·'

const CashAppTooltip: FC = () => {
  const { textSize } = useCheckoutSizes()
  const { t } = useTranslation()
  return <Text size={textSize}>{t('paymentMethod:cashAppPaymentMethodTooltip')}</Text>
}

const PaymentMethodMaskedText: FC<PaymentMethodMaskedTextProps> = ({ text, subtextColor, testNameSpace }) => {
  if (isNullOrUndefined(text)) {
    return null
  }
  return (
    <MaskedElement testNameSpace={testNameSpace}>
      <Span size='XS' color={subtextColor}>
        {text}
      </Span>
    </MaskedElement>
  )
}

export const PaymentMethodInfo: React.FC<PaymentMethodInfoProps> = ({
  paymentMethodMasks,
  hasSeparator = false,
  expiryDate,
  errorKey,
  cashTag,
  disabled = false,
}) => {
  const hasError = errorKey !== undefined && errorKey !== null
  const { subtextColor } = useCheckoutColor({ hasError, isDisabled: disabled })
  const cashAppPaymentMethodTooltipRef = useRef<HTMLSpanElement>(null)
  const { showTooltip, unload } = useTooltip(cashAppPaymentMethodTooltipRef, {
    closeOnOutsideClick: true,
    verticalOffset: 10,
    caretPlacement: 'top',
    bubbleSize: { width: 343 },
  })
  const hasCashTag = !isUndefinedOrEmpty(cashTag)
  useEffect(() => {
    return () => {
      unload()
    }
  }, [])

  const openTooltip = (): void => {
    showTooltip(<CashAppTooltip />)
  }
  return (
    <span className={styles.cardDetails}>
      {hasCashTag && (
        <CashAppPayInfo
          openTooltip={openTooltip}
          subtextColor={subtextColor}
          cashAppPaymentMethodTooltipRef={cashAppPaymentMethodTooltipRef}
          cashTag={cashTag}
        />
      )}
      {!isNullOrUndefined(paymentMethodMasks) && (
        <>
          <CardAsterisk color={subtextColor} disabled={disabled} error={hasError} />
          <PaymentMethodMaskedText text={paymentMethodMasks} subtextColor={subtextColor} testNameSpace='payment-method-masked-text' />
        </>
      )}
      {hasSeparator && (
        <Span size='XS' color={subtextColor}>
          {SEPERATOR}
        </Span>
      )}
      {!isNullOrUndefined(expiryDate) && <PaymentMethodMaskedText text={expiryDate} subtextColor={subtextColor} testNameSpace='expiry-date-masked-text' />}
    </span>
  )
}

interface CashAppPayInfoProps {
  subtextColor: ColorTypes
  cashTag: string | undefined
  cashAppPaymentMethodTooltipRef: React.RefObject<HTMLSpanElement>
  openTooltip: () => void
}

const CashAppPayInfo: FC<CashAppPayInfoProps> = ({ subtextColor, cashTag, cashAppPaymentMethodTooltipRef, openTooltip }) => {
  const cohort = useCohort()
  const { t } = useTranslation()
  const isCashOnly = cohort === Cohort.CashOnly
  const isCardAddEnabled = useFlag(Features.ENABLE_CARD_ADD_FOR_CASH_COHORT, false)
  const applePayEnabled = useApplePayPayment()
  const isApplePayEnabled = useFlag(Features.ENABLE_APPLE_PAY_FOR_CASH_COHORT, false) && applePayEnabled
  const showInfoIcon = isCashOnly && !isCardAddEnabled && !isApplePayEnabled
  return (
    <>
      <PaymentMethodMaskedText text={cashTag} subtextColor={subtextColor} testNameSpace='cash-tag-masked-text' />
      {showInfoIcon && (
        <span className={styles.cashAppPayTooltipContainer} ref={cashAppPaymentMethodTooltipRef}>
          <Button.Link testNameSpace='cash-app-pay-tooltip' size='Small' onClick={openTooltip} aria-label={t('paymentMethod:moreInfo')}>
            <Icon.Info size='Small' />
          </Button.Link>
        </span>
      )}
    </>
  )
}
